import { useState, useRef, useCallback, useEffect } from 'react'

interface AudioLevelDetectionOptions {
  updateInterval?: number // Milliseconds between level updates
  smoothingFactor?: number // 0-1, higher values = more smoothing
  minDecibels?: number // Minimum decibel level to consider
  maxDecibels?: number // Maximum decibel level to consider
}

interface UseAudioLevelDetectionReturn {
  audioLevel: number // 0-100 scale
  isDetecting: boolean
  startDetection: (stream: MediaStream) => void
  stopDetection: () => void
  peakLevel: number
  averageLevel: number
  resetPeak: () => void
  resetAll: () => void
}

export const useAudioLevelDetection = (
  options: AudioLevelDetectionOptions = {}
): UseAudioLevelDetectionReturn => {
  const {
    updateInterval = 50, // Update 20 times per second
    smoothingFactor = 0.8,
    minDecibels = -90,
    maxDecibels = -10
  } = options

  const [audioLevel, setAudioLevel] = useState(0)
  const [isDetecting, setIsDetecting] = useState(false)
  const [peakLevel, setPeakLevel] = useState(0)
  const [averageLevel, setAverageLevel] = useState(0)

  const audioContextRef = useRef<AudioContext | null>(null)
  const analyserRef = useRef<AnalyserNode | null>(null)
  const microphoneRef = useRef<MediaStreamAudioSourceNode | null>(null)
  const animationFrameRef = useRef<number | null>(null)
  const intervalRef = useRef<NodeJS.Timeout | null>(null)
  const dataArrayRef = useRef<Uint8Array<ArrayBuffer> | null>(null)
  const smoothedLevelRef = useRef(0)
  const levelHistoryRef = useRef<number[]>([])

  const calculateAudioLevel = useCallback(() => {
    if (!analyserRef.current || !dataArrayRef.current) return 0

    analyserRef.current.getByteFrequencyData(dataArrayRef.current)
    
    // Calculate RMS (Root Mean Square) for more accurate volume detection
    let sum = 0
    for (let i = 0; i < dataArrayRef.current.length; i++) {
      sum += dataArrayRef.current[i] * dataArrayRef.current[i]
    }
    const rms = Math.sqrt(sum / dataArrayRef.current.length)
    
    // Convert to decibels
    const decibels = 20 * Math.log10(rms / 255)
    
    // Normalize to 0-100 scale
    const normalizedLevel = Math.max(0, Math.min(100, 
      ((decibels - minDecibels) / (maxDecibels - minDecibels)) * 100
    ))

    return normalizedLevel
  }, [minDecibels, maxDecibels])

  const updateAudioLevel = useCallback(() => {
    if (!isDetecting) return

    const currentLevel = calculateAudioLevel()
    
    // Apply smoothing
    smoothedLevelRef.current = (smoothedLevelRef.current * smoothingFactor) + 
                              (currentLevel * (1 - smoothingFactor))
    
    const smoothedLevel = smoothedLevelRef.current

    // Update state
    const roundedLevel = Math.round(smoothedLevel)
    setAudioLevel(roundedLevel)
    
    // Debug logging (only log significant changes)
    if (Math.abs(roundedLevel - (levelHistoryRef.current[levelHistoryRef.current.length - 1] || 0)) > 5) {
      console.log(`Audio level: ${roundedLevel}%, Raw: ${currentLevel.toFixed(1)}%, Smoothed: ${smoothedLevel.toFixed(1)}%`)
    }
    
    // Track peak level
    setPeakLevel(prev => Math.max(prev, smoothedLevel))
    
    // Calculate rolling average (last 20 readings)
    levelHistoryRef.current.push(smoothedLevel)
    if (levelHistoryRef.current.length > 20) {
      levelHistoryRef.current.shift()
    }
    const avgLevel = levelHistoryRef.current.reduce((sum, level) => sum + level, 0) / 
                     levelHistoryRef.current.length
    setAverageLevel(Math.round(avgLevel))

  }, [isDetecting, calculateAudioLevel, smoothingFactor])

  const startDetection = useCallback((stream: MediaStream) => {
    try {
      // Clean up previous detection
      stopDetection()

      // Create audio context
      audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)()
      const audioContext = audioContextRef.current

      // Create analyser
      analyserRef.current = audioContext.createAnalyser()
      analyserRef.current.fftSize = 256
      analyserRef.current.smoothingTimeConstant = 0.3
      
      const bufferLength = analyserRef.current.frequencyBinCount
      dataArrayRef.current = new Uint8Array(bufferLength)

      // Create microphone source
      microphoneRef.current = audioContext.createMediaStreamSource(stream)
      microphoneRef.current.connect(analyserRef.current)

      setIsDetecting(true)
      smoothedLevelRef.current = 0
      levelHistoryRef.current = []

      // Start level detection interval
      intervalRef.current = setInterval(updateAudioLevel, updateInterval)

      console.log('Audio level detection started')

    } catch (error) {
      console.error('Error starting audio level detection:', error)
    }
  }, [updateAudioLevel, updateInterval])

  const stopDetection = useCallback(() => {
    setIsDetecting(false)

    // Clean up interval
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }

    // Clean up animation frame
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current)
      animationFrameRef.current = null
    }

    // Clean up audio nodes
    if (microphoneRef.current) {
      microphoneRef.current.disconnect()
      microphoneRef.current = null
    }

    if (analyserRef.current) {
      analyserRef.current.disconnect()
      analyserRef.current = null
    }

    // Clean up audio context
    if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      audioContextRef.current.close()
      audioContextRef.current = null
    }

    // Reset data but preserve peak and average for display
    dataArrayRef.current = null
    smoothedLevelRef.current = 0
    // Keep levelHistoryRef.current and peak/average values for post-test display
    setAudioLevel(0) // Only reset current level, keep peak and average

    console.log('Audio level detection stopped')
  }, [])

  const resetPeak = useCallback(() => {
    setPeakLevel(0)
  }, [])

  const resetAll = useCallback(() => {
    setPeakLevel(0)
    setAverageLevel(0)
    setAudioLevel(0)
    levelHistoryRef.current = []
  }, [])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopDetection()
    }
  }, [stopDetection])

  return {
    audioLevel,
    isDetecting,
    startDetection,
    stopDetection,
    peakLevel,
    averageLevel,
    resetPeak,
    resetAll
  }
}